import React, { useEffect, useRef, useState } from 'react';
import { Input } from 'components/ui/input';

const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/search';

export function CityAutocomplete({ value, country, onChange, onSelect, placeholder, inputId, testId, required = false }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const inputRef = useRef(null);
  const selectedValueRef = useRef('');

  useEffect(() => {
    const query = value.trim();
    const selectedValue = `${query}|${country || ''}`;
    if (selectedValueRef.current === selectedValue) {
      selectedValueRef.current = '';
      return undefined;
    }
    if (query.length < 1) {
      setSuggestions([]);
      return undefined;
    }

    const controller = new AbortController();
    const timeout = setTimeout(async () => {
      try {
        const params = new URLSearchParams({
          format: 'jsonv2',
          addressdetails: '1',
          limit: '5',
          city: query,
          ...(country ? { country } : {}),
        });
        const response = await fetch(`${NOMINATIM_URL}?${params}`, { signal: controller.signal });
        if (!response.ok) throw new Error('GEOCODING_FAILED');
        const places = await response.json();
        setSuggestions(Array.isArray(places) ? places : []);
        setOpen(true);
      } catch (error) {
        if (error.name !== 'AbortError') setSuggestions([]);
      }
    }, 300);

    return () => {
      controller.abort();
      clearTimeout(timeout);
    };
  }, [value, country]);

  const selectPlace = (place) => {
    const address = place.address || {};
    const city = address.city || address.town || address.village || address.municipality || value;
    const selectedCountry = address.country || country;
    selectedValueRef.current = `${city.trim()}|${selectedCountry || ''}`;
    onSelect({
      city,
      country: selectedCountry,
      latitude: Number(place.lat),
      longitude: Number(place.lon),
    });
    setSuggestions([]);
    setOpen(false);
  };

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        id={inputId}
        required={required}
        value={value}
        onChange={(event) => {
          selectedValueRef.current = '';
          onChange(event.target.value);
        }}
        onFocus={() => suggestions.length > 0 && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder}
        autoComplete="off"
        data-testid={testId}
      />
      {open && suggestions.length > 0 && (
        <ul className="absolute z-20 mt-1 max-h-56 w-full overflow-y-auto rounded-md border bg-popover py-1 text-sm shadow-md">
          {suggestions.map((place) => (
            <li key={place.place_id}>
              <button
                type="button"
                className="w-full px-3 py-2 text-left hover:bg-accent"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => selectPlace(place)}
              >
                {place.display_name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}